import React, { Suspense } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import ReactDOM from 'react-dom/client'

import 'react-toastify/dist/ReactToastify.css'
import 'react-datepicker/dist/react-datepicker.css'
import 'platform-core/styles/core.css'
import './index.css'

import {
  RootContextProvider,
  queryClient,
  QueryContext,
  BadRoute,
  Loader,
  GuardedRoute,
  ErrorPage
} from 'platform-core/ui'
import { COOKIE_NAME } from './util/constants'
import {
  AirframeDetail,
  AirframeInvite,
  Dashboard,
  ForgotPassword,
  Login,
  ResetPassword,
  SettingsHome,
  SignUpNew,
  SignUpInviteExpedited,
  SignUpTransferExpedited,
  SubscriptionInterstitial,
  TermsOfService,
  SignUp
} from './routes'
import LayoutSignUp from './components/Layout/LayoutSignUp'

// Lazy-loading components
const LayoutSidebar = React.lazy(async () => await import('./components/Layout/LayoutSidebar'))
const LayoutOutside = React.lazy(async () => await import('./components/Layout/LayoutOutside'))
const LayoutCopy = React.lazy(async () => await import('./components/Layout/LayoutCopy'))

// Prevent scrolling a number input to increase/decrease the value
document.addEventListener('wheel', function (_) {
  if (document.activeElement != null) {
    try {
      const element = document.activeElement as HTMLInputElement
      if (element.type === 'number') {
        element.blur()
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }
})

const Suspender = (): React.JSX.Element => {
  return (
    <div className="h-full w-full flex items-center justify-center">
      <Loader imageClassName='w-24' extraImageClassName='my-6' />
    </div>
  )
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
)
root.render(
  <React.StrictMode>
    <QueryClientProvider context={QueryContext} client={queryClient}>
      <RootContextProvider cookieName={COOKIE_NAME}>
        <BrowserRouter>
          <Routes>
            {/* Sign Up Routes (Normal flow + Expedited) */}
            <Route path='/auth/sign-up' element={
              <Suspense fallback={<Loader imageClassName='w-24' extraImageClassName='mt-10' />}>
                <LayoutOutside />
              </Suspense>
            }>
              {/* /invite/ is for when an active User invites someone new to the platform to share an airframe with */}
              <Route path='/auth/sign-up/invite/:signUpToken' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><SignUpInviteExpedited /></Suspense>} />
              {/* /transfer/ is for when an active User cancels their subscription and transfers it to someone new to the platform */}
              <Route path='/auth/sign-up/transfer/:transferToken' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><SignUpTransferExpedited /></Suspense>} />

              <Route path='/auth/sign-up' element={<Suspense fallback={<Suspender />}><SignUpNew /></Suspense>} />

            </Route>

            {/* Sign Up Routes (Normal flow + Expedited) */}
            <Route path='/auth/sign-up-old' element={
              <Suspense fallback={<Loader imageClassName='w-24' extraImageClassName='mt-10' />}>
                <LayoutSignUp />
              </Suspense>
            }>
              <Route path='/auth/sign-up-old' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><SignUp /></Suspense>} />
            </Route>

            {/* Copy/Text routes (Terms of Service, etc) */}
            <Route path='/misc' element={
              <Suspense fallback={<Loader imageClassName='w-24' extraImageClassName='mt-10' />}>
                <LayoutCopy />
              </Suspense>
            }>
              <Route path='/misc' element={<BadRoute />} />
              <Route path='/misc/terms-of-service' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><TermsOfService /></Suspense>} />
              <Route path='/misc/*' element={<BadRoute />} />
            </Route>

            {/* Auth routes (login, forgot/reset pw) */}
            <Route path='/auth' element={
              <Suspense fallback={<Loader imageClassName='w-24' extraImageClassName='mt-10' />}>
                <LayoutOutside />
              </Suspense>
            }>
              <Route path='/auth' element={<BadRoute />} />
              <Route path='/auth/login' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><Login /></Suspense>} />
              <Route path='/auth/forgot-password' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><ForgotPassword /></Suspense>} />
              <Route path='/auth/reset-password/:resetToken' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><ResetPassword /></Suspense>} />
              <Route path='/auth/subscription' element={<Suspense fallback={<Loader extraImageClassName='my-6' />}><SubscriptionInterstitial /></Suspense>} />
              <Route path='/auth/*' element={<BadRoute />} />
            </Route>

            {/* Core layout / auth guard */}
            <Route path='/' element={
              <Suspense fallback={<Loader imageClassName='w-24' extraImageClassName='mt-10' />}>
                <GuardedRoute>
                  <LayoutSidebar />
                </GuardedRoute>
              </Suspense>
            }>
              {/* Dashboard */}
              <Route path='/' element={<Suspense fallback={<Loader />}><Dashboard /></Suspense>} />

              {/* Airframes */}
              {/* <Route path='/airframes/new' element={<Suspense fallback={<Loader />}><NewAirframe /></Suspense>} /> */}
              {/* This route is used when an existing User on the platform is invited to a new Airframe */}
              <Route path='/airframes/invite/:inviteId' element={<Suspense fallback={<Loader />}><AirframeInvite /></Suspense>} />
              {/* This route is used when an existing User on the platform has an Airframe transferred */}
              {/* TODO: */}
              {/* <Route path='/airframes/transfer/:transferId' element={<Suspense fallback={<Loader />}><AirframeTransfer /></Suspense>} /> */}
              <Route path='/airframes/:id' element={<Suspense fallback={<Loader />}><AirframeDetail /></Suspense>} />

              {/* Settings */}
              <Route path='/settings' element={<Suspense fallback={<Loader />}><SettingsHome /></Suspense>} />

              {/* other guarded routes... */}
            </Route>

            {/* Error / catch-all routes */}
            <Route path='/error' element={<ErrorPage />} />
            <Route path='*' element={<BadRoute />} />
          </Routes>
        </BrowserRouter>
      </RootContextProvider>
    </QueryClientProvider>
    <ToastContainer hideProgressBar closeOnClick theme='colored' />
  </React.StrictMode>
)
